<script lang="ts" setup>
import { FontAwesomeIcon } from '@shared/plugin/icons';
import { computed, provide } from 'vue';

import VChip from '../VChip/VChip.vue';
import VStyledHtml from '../VStyledHtml/VStyledHtml.vue';
import { type InfoBoxType } from './types';
import { typeInjectionKey } from './keys';

export interface VInfoBoxProps {
  type?: InfoBoxType;
  badgeText?: string;
  size?: 'small' | 'normal';
  customIcon?: string;
}

const { type, badgeText, size = 'normal', customIcon } = defineProps<VInfoBoxProps>();

const wrapperClasses = computed(() => {
  switch (type) {
    case 'info':
      return 'border-info-300 bg-info-200';
    case 'warning':
      return 'border-orange-300 bg-orange-200';
    case 'danger':
      return 'border-red-100 bg-red-50';
    case 'success':
      return 'border-green-100 bg-green-50';
    case 'grey':
      return 'border-grey-300 bg-grey-50';
    default:
      return 'border-grey-200 bg-white';
  }
});

const tagColor = computed(() => {
  switch (type) {
    case 'info':
      return 'blue';
    case 'warning':
      return 'orange';
    case 'danger':
      return 'red';
    case 'success':
      return 'green';
    case 'grey':
      return 'grey';
    default:
      return undefined;
  }
});

const icon = computed(() => {
  if (customIcon) return customIcon;

  switch (type) {
    case 'danger':
    case 'warning':
      return 'far fa-info-circle';
    case 'success':
      return 'far fa-check-circle';
    case 'info':
    default:
      return 'far fa-info-circle';
  }
});

const sizeClasses: Record<typeof size, string> = {
  small: 'px-3 py-2',
  normal: 'px-4 py-6 md:p-8 md:pt-7',
};

provide(typeInjectionKey, { value: type });
</script>

<template>
  <div
    :aria-label="badgeText"
    class="flex rounded border border-solid"
    :class="{
      [sizeClasses[size]]: true,
      [wrapperClasses]: true,
      'gap-2': !badgeText,
      'flex-col gap-4': badgeText,
    }"
    :role="type === 'info' ? 'status' : 'alert'"
  >
    <VChip v-if="badgeText" class="self-start" :color="tagColor" :icon="icon" :label="badgeText" />

    <FontAwesomeIcon v-else class="pt-1" :icon />

    <VStyledHtml class="w-full text-sm text-grey-600">
      <slot></slot>
    </VStyledHtml>

    <ul v-if="$slots.actions" class="grid grow gap-3 p-0">
      <slot name="actions"></slot>
    </ul>
  </div>
</template>
